import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SubTask, Task, TaskTypeAction } from '@k12/models';

/** The figures an admin determines while reviewing an Income Verification (formType 8) submission. */
export interface IncomeVerificationResult {
  verifiedIncome: number | null;
  verifiedHouseholdSize: number | null;
}

/**
 * Admin capture of the Income Verification outcome. Renders beneath the household's read-only
 * submission in the task edit screen.
 *
 * The value is not saved by this component. It is emitted to the parent edit form, which merges it
 * into the sub-task's FormData on save; the API then reads `verifiedIncome` out of the sub-task
 * FormData when the task transitions to Accepted and writes it to the household record
 * (TaskApp.TryPopulateVerifiedIncomeAsync). Accepting the task *is* the "IV = Yes" outcome — a
 * rejected verification moves the task to Denied and never reaches that code path.
 */
@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-income-verification-review-form',
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule],
  templateUrl: './income-verification-review-form.component.html',
  styleUrl: './income-verification-review-form.component.scss'
})
export class IncomeVerificationReviewFormComponent implements OnInit {
  @Input() task: Task | null = null;
  @Input() taskAction: SubTask | null = null;
  @Input() taskTypeAction: TaskTypeAction | null = null;
  @Input() isReadOnly = false;

  @Output() verificationChange = new EventEmitter<IncomeVerificationResult>();
  @Output() validityChange = new EventEmitter<boolean>();

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected form!: FormGroup;

  public ngOnInit(): void {
    const existing = this.readExistingResult();

    this.form = this.fb.group({
      verifiedIncome: [existing.verifiedIncome, [Validators.required, Validators.min(0)]],
      verifiedHouseholdSize: [existing.verifiedHouseholdSize, [Validators.required, Validators.min(1)]]
    });

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    }

    // Emit the seeded value so the parent has it even if the admin never touches the fields
    // (e.g. re-accepting a task whose figures were already entered).
    this.verificationChange.emit(existing);
    this.validityChange.emit(this.form.valid);
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.verificationChange.emit(this.currentResult());
      this.validityChange.emit(this.form.valid);
    });
  }

  private currentResult(): IncomeVerificationResult {
    const raw = this.form.getRawValue();
    return {
      verifiedIncome: this.toNumber(raw.verifiedIncome),
      verifiedHouseholdSize: this.toNumber(raw.verifiedHouseholdSize)
    };
  }

  /**
   * Reads back a previously entered result. Both casings are accepted because the API writes
   * PascalCase into FormData while the Angular forms emit camelCase.
   */
  private readExistingResult(): IncomeVerificationResult {
    const data = this.parseFormData();
    return {
      verifiedIncome: this.toNumber(data['VerifiedIncome'] ?? data['verifiedIncome']),
      verifiedHouseholdSize: this.toNumber(data['VerifiedHouseholdSize'] ?? data['verifiedHouseholdSize'])
    };
  }

  private parseFormData(): Record<string, unknown> {
    const raw = this.taskAction?.formData;
    if (!raw || typeof raw !== 'string') return {};

    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  // Empty strings and unparseable values become null rather than 0, so "not entered" stays
  // distinguishable from a genuine zero.
  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
